import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import { authClient } from "@/lib/auth-client";
import { Check, Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router";
import { useRightPanel } from "@/hooks/useRightPanel";
import FormShell from "@/components/form-shell";

const forgetPasswordSchema = z.object({
  email: z.email({ message: "Email invalide" }),
});

type ForgetPasswordFormValues = z.infer<typeof forgetPasswordSchema>;

export function ForgetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const emailParam = searchParams.get("email") || "";
  const { setRight } = useRightPanel();

  const form = useForm<ForgetPasswordFormValues>({
    resolver: zodResolver(forgetPasswordSchema),
    defaultValues: { email: emailParam },
  });

  // Supprimer le paramètre email de l'URL après initialisation
  useEffect(() => {
    if (emailParam) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("email");
      setSearchParams(newParams, { replace: true });
    }
  }, [emailParam, searchParams, setSearchParams]);

  // Set right panel content
  useEffect(() => {
    setRight(<div className="flex h-full flex-1 items-center justify-center overflow-hidden p-6 text-6xl font-bold">Nautika</div>);
  }, [setRight]);

  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const onSubmit = async (data: ForgetPasswordFormValues) => {
    setIsLoading(true);
    try {
      const { error } = await authClient.requestPasswordReset({
        email: data.email,
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        toast.error("Erreur lors de l'envoi du lien de réinitialisation", { description: error.message });
      } else {
        setEmailSent(true);
      }
    } catch {
      toast.error("Une erreur est survenue");
    }
    setIsLoading(false);
  };
  return (
    <FormShell
      title={emailSent ? "Lien de réinitialisation envoyé" : "Mot de passe oublié"}
      subtitle={
        emailSent
          ? "Si l'adresse est associée à un compte, un email contenant un lien de réinitialisation a été envoyé."
          : "Saisissez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe."
      }
      onSubmit={emailSent ? undefined : form.handleSubmit(onSubmit)}
    >
      <Form {...form}>
        <div className="flex flex-col gap-12">
          {emailSent === false ? (
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="mail@example.com" autoComplete="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : (
            <Check className="mx-auto h-12 w-12 text-green-500" />
          )}
          <Button
            type={emailSent ? "button" : "submit"}
            className="w-full"
            disabled={isLoading}
            onClick={
              emailSent
                ? () => {
                    const email = form.getValues("email");
                    navigate(`/login${email ? `?email=${encodeURIComponent(email)}` : ""}`);
                  }
                : undefined
            }
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Envoi en cours...
              </>
            ) : emailSent ? (
              "Retour à la connexion"
            ) : (
              "Envoyer le lien"
            )}
          </Button>
        </div>
      </Form>
    </FormShell>
  );
}
